export interface AvoClientConfig {
  enrichResponses?: boolean;
  defaultLimit?: number;
}

export interface RequestDefinition {
  route: string;
  params?: Record<string, string>;
  payload?: any;
  method?: 'GET'|'POST'|'PUT'|'PATCH'|'DELETE';
}
export interface RequestConfig {
  retries?: number;
}
export interface EndpointRequestParams {
  subdomain: string;
  after?: string;
  limit?: number;
}
export interface FlashMessage {
  type: 'notice'|'error'|'success';
  message: string;
}
export interface EndpointResponse {
  data: any[];
  lastKey?: string;
  flash?: FlashMessage;
}

export type ExtraRequestParams<Union extends string> = { [param in Union]: string };

export class AvoClient {
  whitelist = [400, 403, 404];

  constructor(public config: AvoClientConfig = {}) {
    this.config.defaultLimit ??= 50;

    this.init();
  }

  private init() { // eslint-disable-line class-methods-use-this
    $.ajaxSetup({
      headers: {
        'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
      },
    });
  }

  ajax<D = any, R = {}>({ route, params, payload, method = 'GET' }: RequestDefinition, { retries = 2 }: RequestConfig = {}): Promise<{ data: D, flash?: FlashMessage }&(R extends never ? {} : R)> {
    return new Promise((resolve, reject) => {
      const data = method === 'GET' ? params : payload;
      let attempt = 0;
      const dispatchRequest = () => $.ajax({
        url: route,
        method,
        data: { enrich: this.config.enrichResponses, ...data },
        success: (res, status, xhr) => {
          const flashType = xhr.getResponseHeader('X-Flash-Type');
          if (!flashType) resolve(res);
          else {
            const flashMessage = xhr.getResponseHeader('X-Flash-Message');
            resolve({ ...res, flash: { type: flashType, message: flashMessage } });
          }
        },
        error: (xhr) => {
          attempt += 1;

          // don't retry on a whitelisted event
          if (attempt <= retries && this.whitelist.indexOf(xhr.status) < 0) {
            setTimeout(() => dispatchRequest(), 2 ** attempt * 1000);
          } else {
            reject(xhr);
          }
        },
      });
      dispatchRequest();
    });
  }

  async tickets({ limit, userId, after, ...rest }: EndpointRequestParams&(
    Partial<ExtraRequestParams<
      'query'|'status'|'userId'|'channel'|'from'|'to'|'order'|'unaddressed'|'unanswered'|'archived'
    >>
  )): Promise<EndpointResponse> {
    const request = {
      route: '/v1/tickets',
      params: {
        limit: limit?.toString?.(),
        user_id: userId,
        filters: after && JSON.stringify([{
          range: {
            "ticket.activity_at": {
              lt: `Date(${after})`,
            },
          },
        }]),
        ...rest,
      },
    };

    const { data: { tickets, last_key: lastKey }, flash } =
      await this.ajax<{ tickets: any[], last_key: string }>(request);
    const response: EndpointResponse = { data: tickets, lastKey, flash };

    return response;
  }

  async events(
    { subdomain, ticket, after, limit }: EndpointRequestParams&{ ticket: string },
  ): Promise<EndpointResponse> {
    const request = {
      route: `/v1/tickets/${ticket}/events`,
      params: {
        subdomain,
        limit: limit?.toString?.(),
        after,
      },
    };

    const { data: { events }, flash } = await this.ajax<{ events: any[] }>(request);
    const response: EndpointResponse = { data: events, flash };
    if (this.config.enrichResponses && (!limit || events.length >= limit)) {
      response.lastKey = events[events.length - 1]?.sent_at?.toString?.();
    }

    return response;
  }
}
